import type { ExpenseFileKind } from "../../expenses/expenseTypes";

/**
 * Almikia Epic EG (TASK-043): file contract of an expense's comprobante and CFDI
 * PDF/XML, shared by the Supabase and FakeRest providers. Private bucket (see
 * `07_storage.sql`), signed URLs only, and no storage update policy: every
 * upload is a NEW object path, never an upsert.
 */

/** Private storage bucket id. */
export const EXPENSE_DOCUMENTS_BUCKET = "expense-documents";

/** Maximum accepted file size, in bytes (10 MiB, same as the bucket limit). */
export const EXPENSE_FILE_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/** A download link lives one minute. */
export const EXPENSE_FILE_SIGNED_URL_EXPIRY_SECONDS = 60;

/** MIME types the bucket accepts. */
const ALLOWED_MIME_TYPES_BY_KIND: Record<ExpenseFileKind, string[]> = {
  receipt: ["application/pdf", "image/jpeg", "image/png", "image/webp"],
  cfdi_pdf: ["application/pdf"],
  cfdi_xml: ["application/xml", "text/xml"],
};

/** Reason code for a rejected upload; the UI owns the Spanish label. */
export type ExpenseFileRejectionReason = "invalid_type" | "too_large";

export type ExpenseFileValidationResult =
  | { valid: true }
  | { valid: false; reason: ExpenseFileRejectionReason };

/** Validates a candidate file for its kind before any network or storage call. */
export function validateExpenseFile(
  kind: ExpenseFileKind,
  file: Pick<File, "type" | "size">,
): ExpenseFileValidationResult {
  if (!ALLOWED_MIME_TYPES_BY_KIND[kind].includes(file.type)) {
    return { valid: false, reason: "invalid_type" };
  }
  if (file.size > EXPENSE_FILE_MAX_SIZE_BYTES) {
    return { valid: false, reason: "too_large" };
  }
  return { valid: true };
}

/**
 * `<kind>/<uuid>-<sanitized name>`: a fresh path per upload (the uuid makes it
 * unique, so an existing object is never overwritten). Only the key segment is
 * sanitized (diacritics stripped, anything outside `[A-Za-z0-9._-]` becomes `_`)
 * because Storage rejects other characters (InvalidKey).
 */
export function buildExpenseFilePath(
  kind: ExpenseFileKind,
  fileName: string,
): string {
  const safeName = fileName
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^A-Za-z0-9._-]/g, "_");
  return `${kind}/${crypto.randomUUID()}-${safeName}`;
}
